import type { Era, Weighted, WeightedName } from '../types.js';

/**
 * Turkish given names and surnames for synthetic profiles.
 *
 * Weights are relative popularity calibrated against TÜİK's most-given-name
 * lists for births 1980–2008 (the app's 18–45 audience), plus the late-2000s
 * trend names a 2005–2008 cohort carries. Era codes per name:
 * p = pre95, a = 95-99, b = 00-04, c = 05-09, d = post09.
 */

const ERA_CODES: Record<string, Era> = { p: 'pre95', a: '95-99', b: '00-04', c: '05-09', d: 'post09' };

function n(name: string, weight: number, eras: string): WeightedName {
  return {
    name,
    weight,
    eras: [...eras].map((c) => {
      const era = ERA_CODES[c];
      if (era === undefined) throw new Error(`Unknown era code '${c}' for ${name}`);
      return era;
    }),
  };
}

function s(name: string, weight: number): Weighted {
  return { name, weight };
}

export const TR_FEMALE_NAMES: WeightedName[] = [
  // Top tier, all cohorts
  n('Zeynep', 100, 'pabcd'), n('Elif', 95, 'pabcd'), n('Merve', 70, 'pab'), n('Fatma', 60, 'pab'),
  n('Ayşe', 55, 'pab'), n('Büşra', 55, 'pab'), n('Esra', 50, 'pab'), n('Kübra', 45, 'pab'),
  n('Emine', 40, 'pa'), n('Hatice', 40, 'pa'), n('Zehra', 35, 'pabcd'), n('Ecrin', 35, 'bcd'),
  n('Azra', 35, 'bcd'), n('Meryem', 30, 'abcd'), n('Rabia', 30, 'abc'), n('Tuğba', 30, 'pa'),
  n('Gizem', 30, 'pa'), n('Yağmur', 30, 'ab'), n('Defne', 30, 'bcd'), n('Eylül', 30, 'bcd'),
  n('Özge', 28, 'pa'), n('Damla', 28, 'pab'), n('Ezgi', 25, 'pa'), n('Cansu', 25, 'pa'),
  n('Sümeyye', 25, 'abc'), n('Buse', 25, 'ab'), n('İrem', 25, 'ab'), n('Asel', 25, 'cd'),
  n('Gamze', 22, 'pa'), n('Burcu', 22, 'p'), n('Melike', 22, 'ab'), n('Ece', 22, 'abc'),
  n('Selin', 22, 'ab'), n('Sıla', 22, 'ab'), n('Aleyna', 22, 'bc'), n('Ceren', 22, 'ab'),
  n('Beyza', 22, 'ab'), n('Sena', 22, 'ab'), n('Esma', 20, 'abc'), n('Seda', 20, 'p'),
  n('Aslı', 20, 'pa'), n('Duygu', 20, 'pa'), n('Nehir', 20, 'bcd'), n('Sude', 20, 'bc'),
  n('Şeyma', 20, 'ab'), n('Tuğçe', 20, 'pa'), n('Esila', 20, 'cd'), n('Asya', 20, 'cd'),
  n('Nisa', 20, 'bc'), n('Derya', 18, 'p'), n('Sinem', 18, 'pa'), n('Pınar', 18, 'p'),
  n('Ebru', 18, 'p'), n('Özlem', 18, 'p'), n('Yasemin', 18, 'pa'), n('Hilal', 18, 'pab'),
  n('Dilara', 18, 'ab'), n('Hiranur', 18, 'cd'), n('Ela', 18, 'bcd'), n('Duru', 18, 'bcd'),
  n('Eslem', 18, 'bcd'), n('Betül', 18, 'pab'), n('Eda', 18, 'pab'), n('İlayda', 18, 'ab'),
  // Mid tier
  n('Miray', 15, 'bc'), n('Ada', 15, 'cd'), n('Beren', 15, 'bc'), n('Belinay', 15, 'cd'),
  n('Zümra', 15, 'bcd'), n('Ebrar', 15, 'bcd'), n('Melek', 15, 'abc'), n('Irmak', 15, 'bc'),
  n('Melis', 15, 'ab'), n('Yaren', 15, 'bc'), n('Elanur', 14, 'bc'), n('Didem', 14, 'p'),
  n('Feyza', 14, 'ab'), n('Melisa', 14, 'ab'), n('Hande', 12, 'pa'), n('Hazal', 12, 'a'),
  n('İpek', 12, 'ab'), n('Nazlı', 12, 'ab'), n('Begüm', 12, 'ab'), n('Meltem', 12, 'p'),
  n('Tuba', 12, 'p'), n('Dilek', 12, 'p'), n('Alya', 12, 'cd'), n('Sudenaz', 12, 'bc'),
  n('Berfin', 12, 'ab'), n('Ecem', 12, 'ab'), n('Gözde', 12, 'pa'), n('Helin', 12, 'bc'),
  n('Öykü', 12, 'ab'), n('Tuana', 12, 'bc'), n('Elif Nur', 12, 'bc'), n('Nur', 10, 'pa'),
  n('Hülya', 10, 'p'), n('Sevgi', 10, 'p'), n('Arzu', 10, 'p'), n('Sibel', 10, 'p'),
  n('Bahar', 10, 'pa'), n('Elisa', 10, 'cd'), n('Aylin', 10, 'pa'), n('Cemre', 10, 'ab'),
  n('Deniz', 10, 'pab'), n('Dilan', 10, 'ab'), n('Kevser', 10, 'ab'), n('Nisanur', 10, 'bc'),
  n('Rümeysa', 10, 'bc'), n('Ayşegül', 10, 'p'), n('Başak', 10, 'pa'), n('Şevval', 10, 'ab'),
  n('Gülşah', 10, 'p'), n('Ceyda', 6, 'pa'), n('Ceylan', 8, 'pa'), n('Nilay', 8, 'pa'),
  n('İdil', 8, 'ab'), n('Ayça', 8, 'pa'), n('Çağla', 8, 'a'), n('Lina', 8, 'd'),
  n('Gülsüm', 8, 'p'), n('Filiz', 8, 'p'), n('Songül', 8, 'p'), n('Leyla', 8, 'p'),
  n('Havva', 8, 'pa'), n('Pelin', 8, 'pa'), n('Serra', 8, 'ab'), n('Simge', 8, 'a'),
  n('Sevda', 8, 'p'), n('Esmanur', 8, 'bc'), n('Ravza', 8, 'cd'), n('Çiğdem', 8, 'p'),
  n('Hümeyra', 8, 'bc'), n('Neslihan', 8, 'pa'), n('Sema', 8, 'p'), n('Nursena', 8, 'ab'),
  n('Ilgın', 6, 'bc'), n('Işıl', 6, 'a'), n('İlknur', 6, 'p'), n('Lara', 6, 'cd'),
  n('Mine', 6, 'p'), n('Müge', 6, 'p'), n('Selen', 6, 'a'), n('Serap', 6, 'p'),
  n('Sultan', 6, 'p'), n('Yeliz', 6, 'p'), n('Gökçe', 6, 'a'), n('Funda', 6, 'p'),
  n('Canan', 6, 'p'), n('Berna', 6, 'p'), n('Aysel', 6, 'p'), n('Aynur', 6, 'p'),
  n('Nurcan', 6, 'p'), n('Serpil', 6, 'p'), n('Hacer', 6, 'p'), n('Zeynep Su', 6, 'cd'),
  n('Hatice Kübra', 6, 'ab'), n('Fatma Nur', 6, 'ab'), n('Ayşe Nur', 6, 'ab'), n('Elif Naz', 6, 'bc'),
  n('Ceylin', 6, 'cd'), n('Beyzanur', 6, 'bc'), n('Kardelen', 6, 'ab'), n('Mina', 6, 'cd'),
  n('Aysu', 6, 'ab'), n('Aybüke', 6, 'ab'), n('Berra', 6, 'bc'), n('Naz', 6, 'bc'),
  n('Sare', 6, 'cd'), n('Semanur', 6, 'bc'), n('Serenay', 6, 'bc'), n('Sevde', 6, 'ab'),
  n('Sümeyra', 6, 'ab'), n('Şeyda', 6, 'ab'), n('Edanur', 6, 'bc'), n('İkra', 6, 'cd'),
  n('Rana', 4, 'ab'), n('Medine', 5, 'ab'), n('Melda', 4, 'a'), n('Mehtap', 5, 'p'),
  n('Nihal', 5, 'p'), n('Nil', 5, 'a'), n('Nilüfer', 5, 'p'), n('Selma', 5, 'p'),
  n('Semra', 5, 'p'), n('Şerife', 5, 'p'), n('Yeşim', 5, 'p'), n('Buket', 5, 'p'),
  n('Emel', 5, 'p'), n('Bengü', 5, 'a'), n('Kader', 5, 'p'), n('İnci', 5, 'p'),
  // Long tail
  n('Sevim', 5, 'p'), n('Gülay', 5, 'p'), n('Nermin', 4, 'p'), n('Tülay', 4, 'p'),
  n('Gül', 4, 'p'), n('Ayten', 4, 'p'), n('Bilge', 6, 'a'), n('Burçin', 4, 'p'),
  n('Dilay', 4, 'a'), n('Dilşad', 4, 'a'), n('Dicle', 4, 'a'), n('Elçin', 4, 'p'),
  n('Elvan', 4, 'p'), n('Esin', 4, 'p'), n('Fadime', 4, 'p'), n('Gaye', 4, 'a'),
  n('Gonca', 4, 'p'), n('Gülnur', 4, 'p'), n('Handan', 4, 'p'), n('Lale', 3, 'p'),
  n('Meral', 4, 'p'), n('Nagihan', 4, 'p'), n('Reyhan', 4, 'p'), n('Rukiye', 4, 'p'),
  n('Selda', 4, 'p'), n('Sevil', 4, 'p'), n('Sevinç', 4, 'p'), n('Seher', 4, 'p'),
  n('Su', 4, 'c'), n('Şebnem', 4, 'p'), n('Şule', 4, 'p'), n('Şura', 4, 'bc'),
  n('Ümmühan', 4, 'pa'), n('Yıldız', 4, 'p'), n('Yonca', 4, 'a'), n('Zuhal', 4, 'p'),
  n('Eda Nur', 4, 'b'), n('Ahsen', 4, 'bc'), n('Almina', 4, 'cd'), n('Arya', 4, 'd'),
  n('Bade', 4, 'bc'), n('Ekin', 4, 'ab'), n('Gülşen', 4, 'p'), n('İclal', 4, 'bc'),
  n('Merve Nur', 4, 'b'), n('Meyra', 4, 'cd'), n('Mira', 4, 'd'), n('Nida', 4, 'bc'),
  n('Nihan', 4, 'p'), n('Reyyan', 4, 'cd'), n('Rojda', 4, 'ab'), n('Ruken', 4, 'ab'),
  n('Sanem', 4, 'a'), n('Sudem', 4, 'bc'), n('Ümmügülsüm', 4, 'ab'), n('Vildan', 4, 'p'),
  n('Zehranur', 4, 'bc'), n('Zeliha', 4, 'p'), n('Nazlıcan', 4, 'ab'), n('Asude', 4, 'bc'),
  n('Ayla', 4, 'p'), n('Aysun', 4, 'p'), n('Cemile', 4, 'p'), n('Cennet', 4, 'pa'),
  n('Figen', 4, 'p'), n('Nesrin', 4, 'p'), n('Nuray', 4, 'p'), n('Nurgül', 4, 'p'),
  n('Saliha', 4, 'p'), n('Seval', 4, 'p'), n('Fulya', 4, 'p'), n('Aybike', 3, 'bc'),
  n('Esengül', 3, 'p'), n('Gülcan', 3, 'p'), n('Gülizar', 3, 'p'), n('Gülten', 3, 'p'),
  n('Habibe', 3, 'p'), n('Hale', 3, 'p'), n('Mihriban', 3, 'p'), n('Nazan', 3, 'p'),
  n('Necla', 3, 'p'), n('Nurdan', 3, 'p'), n('Nurhan', 3, 'p'), n('Nursel', 3, 'p'),
  n('Nurten', 3, 'p'), n('Oya', 3, 'p'), n('Sezen', 3, 'p'), n('Şenay', 3, 'p'),
  n('Tülin', 3, 'p'), n('Türkan', 3, 'p'), n('Ümran', 3, 'p'), n('Zerrin', 3, 'p'),
  n('Şükran', 3, 'p'), n('Dilruba', 3, 'a'), n('Bennu', 3, 'a'), n('Ezel', 3, 'bc'),
  n('Gülnaz', 3, 'pa'), n('Gülsen', 3, 'p'), n('Gülseren', 3, 'p'), n('Hanife', 3, 'p'),
  n('Mehlika', 3, 'bc'), n('Nurseli', 3, 'a'), n('Selvi', 3, 'p'), n('Sevgül', 3, 'p'),
  n('Şirin', 3, 'pa'), n('Tansu', 3, 'p'), n('Tutku', 3, 'a'), n('Yaprak', 3, 'pa'),
  n('Zara', 3, 'cd'), n('Zişan', 3, 'a'), n('Ayfer', 3, 'p'), n('Belgin', 3, 'p'),
  n('Birsen', 3, 'p'), n('Döndü', 3, 'p'), n('Fatoş', 3, 'p'), n('Ferda', 3, 'p'),
  n('Feride', 3, 'p'), n('Gülhan', 3, 'p'), n('Nalan', 3, 'p'), n('Nevin', 3, 'p'),
  n('Özgül', 3, 'p'), n('Sakine', 3, 'p'), n('Semiha', 3, 'p'), n('Sevilay', 3, 'p'),
  n('Gülistan', 3, 'p'), n('Elvin', 3, 'cd'), n('Jale', 2, 'p'), n('Kamile', 2, 'p'),
  n('Güzide', 2, 'p'), n('Hamide', 2, 'p'), n('Hüsniye', 2, 'p'), n('Kezban', 2, 'p'),
  n('Kıymet', 2, 'p'), n('Mukaddes', 2, 'p'), n('Perihan', 2, 'p'), n('Saadet', 2, 'p'),
  n('Zübeyde', 2, 'p'), n('Bihter', 2, 'p'), n('Dide', 2, 'a'), n('Didar', 2, 'a'),
  n('Elmas', 2, 'p'), n('Esen', 2, 'p'), n('Gülbahar', 2, 'p'), n('Gülden', 2, 'p'),
  n('Gülfem', 2, 'p'), n('Gülümser', 2, 'p'), n('Hayriye', 2, 'p'), n('Lal', 2, 'cd'),
  n('Nazife', 2, 'p'), n('Nefise', 2, 'p'), n('Pakize', 2, 'p'), n('Pembe', 2, 'p'),
  n('Seyhan', 2, 'p'), n('Sıdıka', 2, 'p'), n('Sonay', 2, 'p'), n('Yeter', 2, 'p'),
  n('Zerda', 2, 'a'), n('Bediha', 2, 'p'), n('Dürdane', 2, 'p'), n('Lütfiye', 2, 'p'),
  n('Makbule', 2, 'p'), n('Münevver', 2, 'p'), n('Nazmiye', 2, 'p'), n('Özden', 2, 'p'),
  n('Pervin', 2, 'p'), n('Remziye', 2, 'p'), n('Sabriye', 2, 'p'), n('Sevtap', 2, 'p'),
  n('Şükriye', 2, 'p'), n('Zekiye', 2, 'p'), n('Ecrin Su', 3, 'cd'), n('Elif Su', 3, 'cd'),
  n('Duru Su', 2, 'd'), n('Asmin', 3, 'cd'), n('Beril', 4, 'ab'), n('Bensu', 4, 'ab'),
  n('Nilsu', 4, 'bc'), n('Damla Nur', 2, 'b'), n('Elçin Su', 1, 'c'), n('Yüsra', 4, 'bc'),
  n('Esmira', 2, 'cd'), n('Eflin', 2, 'cd'), n('İlkim', 3, 'bc'), n('Tuğçe Nur', 2, 'b'),
];

export const TR_MALE_NAMES: WeightedName[] = [
  // Top tier, all cohorts
  n('Mehmet', 100, 'pabcd'), n('Mustafa', 90, 'pabcd'), n('Ahmet', 80, 'pabcd'), n('Ali', 60, 'pabcd'),
  n('Yusuf', 60, 'pabcd'), n('Hüseyin', 50, 'pab'), n('Hasan', 50, 'pab'), n('Emre', 45, 'pab'),
  n('Muhammed', 45, 'abcd'), n('İbrahim', 40, 'pab'), n('Murat', 40, 'p'), n('Burak', 40, 'pab'),
  n('Furkan', 40, 'ab'), n('Enes', 40, 'ab'), n('İsmail', 35, 'pab'), n('Ömer', 35, 'pabcd'),
  n('Fatih', 35, 'pa'), n('Osman', 30, 'pab'), n('Abdullah', 30, 'pab'), n('Emirhan', 30, 'abc'),
  n('Emir', 30, 'bcd'), n('Eymen', 30, 'cd'), n('Ramazan', 25, 'pab'), n('Batuhan', 25, 'ab'),
  n('Efe', 25, 'bcd'), n('Berat', 25, 'bcd'), n('Eren', 25, 'abc'), n('Yasin', 22, 'ab'),
  n('Mert', 22, 'ab'), n('Kerem', 22, 'bcd'), n('Arda', 22, 'bc'), n('Halil', 20, 'pa'),
  n('Hakan', 20, 'p'), n('Yunus Emre', 20, 'ab'), n('Ömer Faruk', 20, 'abc'), n('Yiğit', 20, 'bcd'),
  n('Mahmut', 18, 'pa'), n('Kadir', 18, 'pa'), n('Serkan', 18, 'p'), n('Gökhan', 18, 'p'),
  n('Oğuzhan', 18, 'ab'), n('Berkay', 18, 'ab'), n('Onur', 18, 'pa'), n('Uğur', 18, 'pa'),
  n('Hamza', 18, 'bcd'), n('Miraç', 18, 'cd'), n('Alperen', 18, 'ab'),
  // Mid tier
  n('Recep', 15, 'pa'), n('Can', 15, 'pab'), n('Kaan', 15, 'ab'), n('Berk', 15, 'ab'),
  n('Muhammet', 15, 'ab'), n('Umut', 15, 'ab'), n('Barış', 15, 'pa'), n('Süleyman', 15, 'pab'),
  n('Volkan', 12, 'p'), n('Baran', 12, 'bc'), n('Muhammed Emin', 12, 'cd'), n('Alparslan', 12, 'cd'),
  n('Salih', 12, 'pab'), n('Taha', 12, 'bcd'), n('Yunus', 12, 'pab'), n('Ömer Asaf', 10, 'cd'),
  n('Yağız', 10, 'cd'), n('Kemal', 10, 'p'), n('Selim', 10, 'pab'), n('Sinan', 10, 'p'),
  n('Tolga', 10, 'p'), n('Ozan', 10, 'pa'), n('Emrah', 10, 'p'), n('Cem', 10, 'pa'),
  n('Bilal', 10, 'ab'), n('Erkan', 10, 'p'), n('Ferhat', 10, 'pa'), n('Harun', 10, 'pab'),
  n('Musa', 10, 'pab'), n('Özgür', 10, 'p'), n('Semih', 10, 'pa'), n('Serdar', 10, 'p'),
  n('Serhat', 10, 'pa'), n('Talha', 10, 'bc'), n('Ümit', 10, 'p'), n('Doğukan', 10, 'ab'),
  n('Ege', 10, 'bc'), n('Metehan', 10, 'ab'), n('Samet', 10, 'ab'), n('Aras', 8, 'cd'),
  n('Göktuğ', 8, 'cd'), n('Çınar', 8, 'cd'), n('Poyraz', 8, 'cd'), n('Ayaz', 8, 'd'),
  n('Kuzey', 8, 'cd'), n('Muhammed Ali', 8, 'cd'), n('Yavuz', 8, 'p'), n('Levent', 8, 'p'),
  n('Oğuz', 8, 'pa'), n('Bayram', 8, 'p'), n('Ercan', 8, 'p'), n('Erdem', 8, 'pa'),
  n('Ertuğrul', 8, 'pab'), n('Görkem', 8, 'a'), n('İlyas', 8, 'ab'), n('Mesut', 8, 'p'),
  n('Selçuk', 8, 'p'), n('Sefa', 8, 'ab'), n('Ufuk', 8, 'pa'), n('Yakup', 8, 'pab'),
  n('Alper', 8, 'pa'), n('Anıl', 8, 'a'), n('Atakan', 8, 'ab'), n('Bekir', 8, 'p'),
  n('Berkan', 8, 'ab'), n('Berke', 8, 'ab'), n('Caner', 8, 'pa'), n('Deniz', 8, 'pab'),
  n('Eyüp', 8, 'pab'), n('Mehmet Ali', 8, 'pab'), n('Mehmet Akif', 8, 'abc'), n('Mücahit', 8, 'ab'),
  n('Tunahan', 8, 'ab'), n('Abdulkadir', 8, 'pab'), n('Abdurrahman', 8, 'ab'), n('Adem', 8, 'pa'),
  n('Fırat', 8, 'pa'), n('Bedirhan', 8, 'bc'), n('Çağrı', 8, 'pa'), n('Doruk', 6, 'bc'),
  n('Yusuf Efe', 6, 'cd'), n('Tuna', 6, 'bc'), n('Cengiz', 6, 'p'), n('Engin', 6, 'p'),
  n('Faruk', 6, 'p'), n('İlker', 6, 'p'), n('Okan', 6, 'p'), n('Orhan', 6, 'p'),
  n('Bülent', 6, 'p'), n('Cihan', 6, 'pa'), n('Doğan', 6, 'p'), n('Erhan', 6, 'p'),
  n('Ersin', 6, 'p'), n('İsa', 6, 'pab'), n('Kenan', 6, 'p'), n('Koray', 6, 'pa'),
  n('Metin', 6, 'p'), n('Melih', 6, 'ab'), n('Sedat', 6, 'p'), n('Sercan', 6, 'pa'),
  n('Seyit', 6, 'pa'), n('Soner', 6, 'p'), n('Taner', 6, 'p'), n('Tarık', 6, 'pa'),
  n('Veli', 6, 'p'), n('Zafer', 6, 'p'), n('Aykut', 6, 'p'), n('Bora', 6, 'ab'),
  n('Buğra', 6, 'ab'), n('Cemal', 6, 'p'), n('Çağatay', 6, 'a'), n('Çağlar', 6, 'p'),
  n('Emin', 6, 'pab'), n('Eray', 6, 'a'), n('Erdal', 6, 'p'), n('Gökberk', 6, 'ab'),
  n('Mehmet Emin', 6, 'ab'), n('Mikail', 6, 'bcd'), n('Oğulcan', 6, 'ab'), n('Savaş', 6, 'p'),
  n('Selman', 6, 'ab'), n('Uğurcan', 6, 'ab'), n('Utku', 6, 'ab'), n('Yahya', 6, 'pab'),
  n('Kağan', 6, 'bc'), n('Bahadır', 5, 'p'), n('Coşkun', 5, 'p'), n('Erdoğan', 5, 'p'),
  n('Kerim', 5, 'pab'), n('Mevlüt', 5, 'p'), n('Selahattin', 5, 'p'),
  // Long tail
  n('Erol', 4, 'p'), n('Cüneyt', 4, 'p'), n('Gökay', 4, 'a'), n('İlhan', 4, 'p'),
  n('Kürşat', 4, 'pa'), n('Nihat', 4, 'p'), n('Oktay', 4, 'p'), n('Rıdvan', 4, 'pa'),
  n('Sadık', 4, 'p'), n('Sami', 4, 'p'), n('Sezer', 4, 'p'), n('Şahin', 4, 'p'),
  n('Şenol', 4, 'p'), n('Şükrü', 4, 'p'), n('Tayfun', 4, 'p'), n('Tuncay', 4, 'p'),
  n('Turgay', 4, 'p'), n('Vedat', 4, 'p'), n('Yalçın', 4, 'p'), n('Yücel', 4, 'p'),
  n('Zeki', 4, 'p'), n('Zekeriya', 4, 'pa'), n('Alp', 4, 'ab'), n('Ataberk', 4, 'bc'),
  n('Atilla', 4, 'p'), n('Ayhan', 4, 'p'), n('Batın', 4, 'bc'), n('Cenk', 4, 'p'),
  n('Egemen', 4, 'ab'), n('Erdi', 4, 'a'), n('Ertan', 4, 'p'), n('Hamit', 4, 'p'),
  n('Haydar', 4, 'p'), n('İlkay', 4, 'a'), n('İrfan', 4, 'p'), n('Kubilay', 4, 'pa'),
  n('Mert Can', 4, 'ab'), n('Muharrem', 4, 'p'), n('Onurcan', 4, 'ab'), n('Özcan', 4, 'p'),
  n('Özkan', 4, 'p'), n('Rüzgar', 4, 'cd'), n('Sait', 4, 'p'), n('Sertaç', 4, 'pa'),
  n('Suat', 4, 'p'), n('Tamer', 4, 'p'), n('Toprak', 4, 'cd'), n('Tuğrul', 4, 'pa'),
  n('Yaşar', 4, 'p'), n('Akif', 4, 'ab'), n('Aziz', 4, 'p'), n('Davut', 4, 'p'),
  n('Ekrem', 4, 'p'), n('Enis', 4, 'ab'), n('Erkam', 4, 'bc'), n('Gürkan', 4, 'pa'),
  n('Halit', 4, 'p'), n('Kıvanç', 4, 'a'), n('Nurullah', 4, 'pa'), n('Safa', 4, 'ab'),
  n('Siraç', 4, 'ab'), n('Umutcan', 4, 'ab'), n('Yılmaz', 4, 'p'), n('Yüksel', 4, 'p'),
  n('Burhan', 4, 'p'), n('Kayra', 4, 'cd'), n('Arif', 4, 'p'), n('Ayberk', 4, 'ab'),
  n('Taylan', 4, 'pa'), n('Ali Can', 4, 'bc'), n('Emre Can', 4, 'ab'), n('Atlas', 4, 'd'),
  n('Aybars', 4, 'cd'), n('Yiğit Efe', 4, 'cd'), n('Polat', 3, 'bc'), n('Ergün', 3, 'p'),
  n('Fevzi', 3, 'p'), n('Feyyaz', 3, 'p'), n('Güray', 3, 'p'), n('Hamdi', 3, 'p'),
  n('Hayri', 3, 'p'), n('Hikmet', 3, 'p'), n('Hilmi', 3, 'p'), n('Nail', 3, 'p'),
  n('Necati', 3, 'p'), n('Nuri', 3, 'p'), n('Orçun', 3, 'a'), n('Rıza', 3, 'p'),
  n('Turan', 3, 'p'), n('Tayyip', 3, 'ab'), n('Baha', 3, 'a'), n('Çağan', 3, 'bc'),
  n('Enver', 3, 'p'), n('Fahri', 3, 'p'), n('Fikret', 3, 'p'), n('Güven', 3, 'p'),
  n('Hakkı', 3, 'p'), n('Hüsnü', 3, 'p'), n('İzzet', 3, 'p'), n('Kamil', 3, 'p'),
  n('Kutay', 3, 'bc'), n('Muhsin', 3, 'p'), n('Nadir', 3, 'p'), n('Necip', 3, 'p'),
  n('Nevzat', 3, 'p'), n('Nurettin', 3, 'p'), n('Nusret', 3, 'p'), n('Orkun', 3, 'a'),
  n('Ömür', 3, 'a'), n('Önder', 3, 'p'), n('Raşit', 3, 'p'), n('Serhan', 3, 'p'),
  n('Sezgin', 3, 'p'), n('Şafak', 3, 'p'), n('Şamil', 3, 'b'), n('Şerif', 3, 'p'),
  n('Tahsin', 3, 'p'), n('Tarkan', 3, 'p'), n('Tevfik', 3, 'p'), n('Türker', 3, 'a'),
  n('Adil', 3, 'p'), n('Cafer', 3, 'p'), n('Dursun', 3, 'p'), n('Esat', 3, 'ab'),
  n('Eyüphan', 3, 'ab'), n('Hacı', 3, 'p'), n('Hidayet', 3, 'p'), n('Lokman', 3, 'pa'),
  n('Mazlum', 3, 'pa'), n('Mehdi', 3, 'b'), n('Mümin', 3, 'b'), n('Nazım', 3, 'p'),
  n('Necmettin', 3, 'p'), n('Nedim', 3, 'p'), n('Sabri', 3, 'p'), n('Sefer', 3, 'p'),
  n('Serdal', 3, 'p'), n('Şahan', 3, 'a'), n('Tunç', 3, 'a'), n('Zeynel', 3, 'p'),
  n('Cebrail', 3, 'b'), n('Mirza', 3, 'cd'), n('Berkcan', 3, 'ab'), n('Tugay', 3, 'a'),
  n('Rahmi', 2, 'p'), n('Reşat', 2, 'p'), n('Teoman', 2, 'p'), n('Yekta', 2, 'b'),
  n('Zülfikar', 2, 'p'), n('Cumali', 2, 'p'), n('Erdinç', 2, 'p'), n('Latif', 2, 'p'),
  n('Muammer', 2, 'p'), n('Rasim', 2, 'p'), n('Rauf', 2, 'p'), n('Rifat', 2, 'p'),
  n('Şevket', 2, 'p'), n('Hıdır', 2, 'p'), n('Rüstem', 2, 'p'), n('Bünyamin', 6, 'ab'),
  n('Abdulsamet', 3, 'ab'), n('Yasin Emre', 2, 'b'), n('Ahmet Emin', 2, 'bc'), n('Ömer Halis', 2, 'b'),
  n('Kaan Efe', 2, 'cd'), n('Emir Ali', 2, 'cd'), n('Eyüp Sultan', 2, 'bc'), n('Musab', 2, 'bc'),
  n('Erdem Can', 2, 'ab'), n('Furkan Can', 1, 'ab'), n('Ali Osman', 2, 'p'), n('Hasan Hüseyin', 3, 'p'),
];

export const TR_SURNAMES: Weighted[] = [
  s('Yılmaz', 100), s('Kaya', 70), s('Demir', 65), s('Çelik', 60), s('Şahin', 58),
  s('Yıldız', 55), s('Yıldırım', 50), s('Öztürk', 48), s('Aydın', 47), s('Özdemir', 46),
  s('Arslan', 44), s('Doğan', 43), s('Kılıç', 40), s('Aslan', 38), s('Çetin', 37),
  s('Kara', 36), s('Koç', 35), s('Kurt', 34), s('Özkan', 33), s('Şimşek', 32),
  s('Polat', 31), s('Özcan', 30), s('Korkmaz', 29), s('Çakır', 28), s('Erdoğan', 27),
  s('Yavuz', 26), s('Can', 25), s('Acar', 25), s('Şen', 24), s('Aktaş', 24),
  s('Güler', 23), s('Yalçın', 23), s('Güneş', 22), s('Bozkurt', 22), s('Bulut', 21),
  s('Keskin', 21), s('Ünal', 20), s('Turan', 20), s('Gül', 20), s('Özer', 19),
  s('Işık', 19), s('Kaplan', 19), s('Avcı', 18), s('Sarı', 18), s('Tekin', 18),
  s('Taş', 18), s('Köse', 17), s('Yüksel', 17), s('Ateş', 17), s('Aksoy', 16),
  s('Yiğit', 16), s('Uçar', 16), s('Çiftçi', 15), s('Karaca', 15), s('Altun', 15),
  s('Erdem', 15), s('Kahraman', 14), s('Aydoğan', 14), s('Akın', 14), s('Çınar', 14),
  s('Sönmez', 13), s('Demirci', 13), s('Uysal', 13), s('Akbulut', 13), s('Ay', 12),
  s('Karakaya', 12), s('Bayram', 12), s('Toprak', 12), s('Önal', 12), s('Tunç', 11),
  s('Kalkan', 11), s('Kılınç', 11), s('Altıntaş', 11), s('Eren', 11), s('Güven', 10),
  s('Durmaz', 10), s('Soylu', 10), s('Sezer', 10), s('Çolak', 10), s('Çelebi', 10),
  s('Koçak', 10), s('Coşkun', 10), s('Duman', 10), s('Bilgin', 9), s('Gündoğdu', 9),
  s('Özgür', 9), s('Akgül', 9), s('Tosun', 9), s('Türk', 9), s('Kartal', 9),
  s('Ekinci', 9), s('Çalışkan', 9), s('Bilgiç', 8), s('Ergün', 8), s('Karataş', 8),
  s('Akkaya', 8), s('Aydemir', 8), s('Oral', 8), s('Tuncer', 8), s('Demirtaş', 8),
  s('Baysal', 7), s('Kurtuluş', 7), s('Orhan', 7), s('Ok', 7), s('Akdoğan', 7),
  s('Bal', 7), s('Bakır', 7), s('Savaş', 7), s('Dinç', 7), s('Sevim', 7),
  s('Gök', 7), s('Gümüş', 7), s('Akbaş', 7), s('Yaman', 7), s('Uzun', 7),
  s('Kocaman', 6), s('Alkan', 6), s('Alp', 6), s('Altay', 6), s('Arı', 6),
  s('Atalay', 6), s('Aşık', 6), s('Balcı', 6), s('Başaran', 6), s('Bayraktar', 6),
  s('Bektaş', 6), s('Cengiz', 6), s('Cömert', 6), s('Çakmak', 6), s('Çam', 6),
  s('Çoban', 6), s('Çakar', 5), s('Dağ', 5), s('Demirel', 5), s('Deniz', 5),
  s('Dikmen', 5), s('Doğru', 5), s('Dündar', 5), s('Ekici', 5), s('Elmas', 5),
  s('Erol', 5), s('Ersoy', 5), s('Ertürk', 5), s('Esen', 5), s('Eser', 5),
  s('Genç', 5), s('Göksu', 5), s('Gökçe', 5), s('Güçlü', 5), s('Gündüz', 5),
  s('Güngör', 5), s('Gürbüz', 5), s('Gürel', 5), s('Gürsoy', 5), s('Güzel', 5),
  s('Hacıoğlu', 5), s('İnce', 5), s('Kandemir', 5), s('Karabulut', 5), s('Karadağ', 5),
  s('Karadeniz', 4), s('Karagöz', 4), s('Karahan', 4), s('Karakuş', 4), s('Karaman', 4),
  s('Kaymak', 4), s('Keleş', 4), s('Kırmızı', 4), s('Kocabaş', 4), s('Kuru', 4),
  s('Kuş', 4), s('Mert', 4), s('Metin', 4), s('Oğuz', 4), s('Okur', 4),
  s('Oruç', 4), s('Önder', 4), s('Öner', 4), s('Özbek', 4), s('Özdoğan', 4),
  s('Özen', 4), s('Özgen', 4), s('Özkaya', 4), s('Öztekin', 4), s('Özyurt', 4),
  s('Parlak', 4), s('Sağlam', 4), s('Sarıkaya', 4), s('Saygılı', 4), s('Selçuk', 4),
  s('Sevinç', 3), s('Sezgin', 3), s('Soydan', 3), s('Sözen', 3), s('Sungur', 3),
  s('Şeker', 3), s('Şener', 3), s('Şenol', 3), s('Tan', 3), s('Taşdemir', 3),
  s('Taşkın', 3), s('Tatar', 3), s('Temel', 3), s('Tezcan', 3), s('Tokgöz', 3),
  s('Topal', 3), s('Topçu', 3), s('Tuna', 3), s('Turgut', 3), s('Tutar', 3),
  s('Türkmen', 3), s('Uğur', 3), s('Uslu', 3), s('Uyar', 3), s('Ülger', 3),
  s('Ünlü', 3), s('Vural', 3), s('Yaşar', 3), s('Yazıcı', 3), s('Yener', 3),
  s('Yeşil', 3), s('Yetkin', 3), s('Yurt', 3), s('Yurdakul', 3), s('Yücel', 3),
  s('Zengin', 3), s('Akyol', 3), s('Akyüz', 3), s('Alemdar', 2), s('Aras', 2),
  s('Arıkan', 2), s('Arıcı', 2), s('Atay', 2), s('Ayhan', 2), s('Aykaç', 2),
  s('Bağcı', 2), s('Başar', 2), s('Bingöl', 2), s('Bozkaya', 2), s('Budak', 2),
  s('Cebeci', 2), s('Ceylan', 2), s('Çağlar', 2), s('Çiçek', 2), s('Ercan', 2),
  s('Gedik', 2), s('Gültekin', 2), s('Günay', 2), s('İpek', 2), s('Kalaycı', 2),
  s('Karabacak', 2), s('Kaynak', 2), s('Kocaoğlu', 2), s('Mutlu', 2), s('Okumuş', 2),
  s('Özçelik', 2), s('Seçkin', 2), s('Şanlı', 2), s('Taşçı', 2), s('Temiz', 2),
  s('Toker', 2), s('Turhan', 2), s('Uğurlu', 2), s('Yalın', 2), s('Yalçınkaya', 2),
  s('Yazar', 2), s('Ekşi', 2), s('Kocatepe', 1), s('Dursun', 2), s('Ayaz', 2),
];
